/**
 * ProfileCompletionBanner
 * -----------------------
 * Shown at the top of the ClientDashboard (Parent tab + SingleView) when
 * the member has unfilled "fill-in-later" fields after a low-friction
 * signup (2026-06-09 spec).
 *
 * Lists what's missing — phone, address, emergency contact, athlete DOB,
 * athlete track — with a single inline modal that PATCHes each field
 * independently. Auto-dismisses (returns null) once nothing's missing.
 *
 * Props:
 *   - user       : enriched profile object (from GET /api/profile)
 *   - onUpdated  : () => void   called after a successful save so the
 *                  parent can refetch /profile.
 */
import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    UserCog, X, Phone, MapPin, HeartPulse, Cake, Target, Loader2,
    CheckCircle2, ChevronRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Canonical track values — must match backend VALID_TRACKS in
// routes/profile.py and the FE source-of-truth in AthleteEditorModal.jsx.
// Duplicated here (not imported) to keep the banner self-contained; if you
// change these, also change AthleteEditorModal.TRACKS and backend.VALID_TRACKS.
const TRACK_OPTIONS = [
    { v: "school_foundational", label: "Foundational Athleticism (8–10)" },
    { v: "school_elite_youth", label: "Elite Youth Performance (11–14)" },
    { v: "school_elite_peak", label: "Elite Peak Performance (14–18)" },
    { v: "pursuing_pro", label: "Pursuing Pro (18–35)" },
    { v: "athletic_aesthetic", label: "Adult Performance" },
];

export default function ProfileCompletionBanner({ user, onUpdated }) {
    const missing = useMemo(() => computeMissing(user), [user]);
    const [openField, setOpenField] = useState(null);

    if (missing.length === 0) return null;

    return (
        <>
            <section
                data-testid="profile-completion-banner"
                className="mx-5 my-3 border border-amber-700/60 bg-amber-950/30 rounded-lg px-4 py-3"
            >
                <div className="flex items-center gap-3 mb-2">
                    <UserCog className="w-4 h-4 text-amber-300 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-amber-100">Complete your profile</div>
                        <div className="text-[11px] text-amber-200/70 mt-0.5">
                            {missing.length} thing{missing.length === 1 ? "" : "s"} left — takes &lt; 30 seconds each.
                        </div>
                    </div>
                </div>
                <div className="space-y-1.5">
                    {missing.map((m) => {
                        const Icon = m.icon;
                        return (
                            <button
                                type="button"
                                key={m.key}
                                onClick={() => setOpenField(m)}
                                data-testid={`complete-${m.key}`}
                                className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-left hover:bg-amber-900/30 transition-colors"
                            >
                                <Icon className="w-3.5 h-3.5 text-amber-300/80 flex-shrink-0" />
                                <span className="text-xs text-amber-100 flex-1 min-w-0 truncate">
                                    {m.label}
                                </span>
                                <ChevronRight className="w-3.5 h-3.5 text-amber-200/60 flex-shrink-0" />
                            </button>
                        );
                    })}
                </div>
            </section>

            {openField && (
                <FieldEditorModal
                    field={openField}
                    user={user}
                    onClose={() => setOpenField(null)}
                    onSaved={() => { setOpenField(null); onUpdated?.(); }}
                />
            )}
        </>
    );
}


/* ─────────────────────────── Missing-field computer ────────────────────── */

function computeMissing(user) {
    const out = [];
    if (!user) return out;
    if (!(user.phone || "").trim()) {
        out.push({ key: "phone", label: "Add a phone number", icon: Phone, kind: "phone" });
    }
    const addr = user.address || {};
    if (!(addr.line1 || "").trim() || !(addr.city || "").trim() || !(addr.state || "").trim()) {
        out.push({ key: "address", label: "Add your address", icon: MapPin, kind: "address" });
    }
    const ec = user.emergency_contact || {};
    if (!(ec.name || "").trim() || !(ec.phone || "").trim() || !(ec.relationship || "").trim()) {
        out.push({ key: "emergency", label: "Add an emergency contact", icon: HeartPulse, kind: "emergency" });
    }
    const athletes = user.athletes || [];
    for (const a of athletes) {
        if (!a.dob) {
            out.push({
                key: `athlete-dob-${a.athlete_id}`,
                label: `${a.first_name}: add date of birth`,
                icon: Cake,
                kind: "athlete-dob",
                athlete: a,
            });
        }
        if (!a.track) {
            out.push({
                key: `athlete-track-${a.athlete_id}`,
                label: `${a.first_name}: pick a track`,
                icon: Target,
                kind: "athlete-track",
                athlete: a,
            });
        }
    }
    return out;
}


/* ─────────────────────────── Inline editor modal ────────────────────── */

function FieldEditorModal({ field, user, onClose, onSaved }) {
    const [busy, setBusy] = useState(false);

    // Local state per kind — initialised from the user object.
    const [phone, setPhone] = useState(user.phone || "");
    const [addr, setAddr] = useState({
        line1: user.address?.line1 || "",
        line2: user.address?.line2 || "",
        city: user.address?.city || "",
        state: user.address?.state || "",
        postal_code: user.address?.postal_code || "",
    });
    const [ec, setEc] = useState({
        name: user.emergency_contact?.name || "",
        phone: user.emergency_contact?.phone || "",
        relationship: user.emergency_contact?.relationship || "",
    });
    const [athleteEdit, setAthleteEdit] = useState({
        dob: field.athlete?.dob || "",
        track: field.athlete?.track || "",
    });

    const save = async () => {
        setBusy(true);
        try {
            if (field.kind === "phone") {
                if (!phone.trim()) throw new Error("Phone can't be empty.");
                await axios.patch(`${API}/profile`, { phone }, { withCredentials: true });
            } else if (field.kind === "address") {
                if (!addr.line1.trim() || !addr.city.trim() || !addr.state.trim()) {
                    throw new Error("Street, city, and state are required.");
                }
                await axios.patch(`${API}/profile`, { address: addr }, { withCredentials: true });
            } else if (field.kind === "emergency") {
                if (!ec.name.trim() || !ec.phone.trim() || !ec.relationship.trim()) {
                    throw new Error("All three emergency-contact fields are required.");
                }
                await axios.patch(`${API}/profile`, { emergency_contact: ec }, { withCredentials: true });
            } else if (field.kind === "athlete-dob") {
                if (!athleteEdit.dob) throw new Error("Pick a date.");
                await axios.patch(
                    `${API}/profile/athletes/${field.athlete.athlete_id}`,
                    { dob: athleteEdit.dob },
                    { withCredentials: true },
                );
            } else if (field.kind === "athlete-track") {
                if (!athleteEdit.track) throw new Error("Pick a track.");
                await axios.patch(
                    `${API}/profile/athletes/${field.athlete.athlete_id}`,
                    { track: athleteEdit.track },
                    { withCredentials: true },
                );
            }
            toast.success("Saved", { description: "Thanks — your profile is up to date." });
            onSaved();
        } catch (e) {
            toast.error("Couldn't save", { description: e?.response?.data?.detail || e.message });
        } finally {
            setBusy(false);
        }
    };

    const inputCls =
        "w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-red";
    const labelCls = "text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1 block";

    return (
        <div
            className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={onClose}
            data-testid="profile-completion-modal"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-zinc-900 border border-zinc-800 rounded-t-2xl sm:rounded-xl w-full sm:max-w-md p-5 max-h-[90vh] overflow-y-auto"
            >
                <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="min-w-0">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Profile</div>
                        <h2 className="text-lg font-semibold text-white mt-0.5">{field.label}</h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="close-completion-modal"
                        className="text-zinc-500 hover:text-white -m-1 p-1"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {field.kind === "phone" && (
                    <div>
                        <label className={labelCls}>Phone</label>
                        <input
                            type="tel"
                            data-testid="banner-phone-input"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="(555) 555-5555"
                            className={inputCls}
                            autoFocus
                        />
                    </div>
                )}

                {field.kind === "address" && (
                    <div className="space-y-3">
                        <div>
                            <label className={labelCls}>Street</label>
                            <input
                                type="text"
                                data-testid="banner-addr-line1"
                                value={addr.line1}
                                onChange={(e) => setAddr({ ...addr, line1: e.target.value })}
                                className={inputCls}
                                autoFocus
                            />
                        </div>
                        <div>
                            <label className={labelCls}>Apt / Suite (optional)</label>
                            <input
                                type="text"
                                value={addr.line2}
                                onChange={(e) => setAddr({ ...addr, line2: e.target.value })}
                                className={inputCls}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelCls}>City</label>
                                <input
                                    type="text"
                                    data-testid="banner-addr-city"
                                    value={addr.city}
                                    onChange={(e) => setAddr({ ...addr, city: e.target.value })}
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className={labelCls}>State</label>
                                <input
                                    type="text"
                                    data-testid="banner-addr-state"
                                    value={addr.state}
                                    onChange={(e) => setAddr({ ...addr, state: e.target.value })}
                                    maxLength={2}
                                    placeholder="TX"
                                    className={inputCls}
                                />
                            </div>
                        </div>
                        <div>
                            <label className={labelCls}>Postal code</label>
                            <input
                                type="text"
                                value={addr.postal_code}
                                onChange={(e) => setAddr({ ...addr, postal_code: e.target.value })}
                                className={inputCls}
                            />
                        </div>
                    </div>
                )}

                {field.kind === "emergency" && (
                    <div className="space-y-3">
                        <div>
                            <label className={labelCls}>Full name</label>
                            <input
                                type="text"
                                data-testid="banner-ec-name"
                                value={ec.name}
                                onChange={(e) => setEc({ ...ec, name: e.target.value })}
                                className={inputCls}
                                autoFocus
                            />
                        </div>
                        <div>
                            <label className={labelCls}>Phone</label>
                            <input
                                type="tel"
                                data-testid="banner-ec-phone"
                                value={ec.phone}
                                onChange={(e) => setEc({ ...ec, phone: e.target.value })}
                                className={inputCls}
                            />
                        </div>
                        <div>
                            <label className={labelCls}>Relationship</label>
                            <input
                                type="text"
                                data-testid="banner-ec-relation"
                                value={ec.relationship}
                                onChange={(e) => setEc({ ...ec, relationship: e.target.value })}
                                placeholder="Spouse, sibling, family friend"
                                className={inputCls}
                            />
                        </div>
                    </div>
                )}

                {field.kind === "athlete-dob" && (
                    <div>
                        <label className={labelCls}>Date of birth</label>
                        <input
                            type="date"
                            data-testid="banner-athlete-dob"
                            value={athleteEdit.dob}
                            onChange={(e) => setAthleteEdit({ ...athleteEdit, dob: e.target.value })}
                            className={inputCls}
                            autoFocus
                        />
                    </div>
                )}

                {field.kind === "athlete-track" && (
                    <div>
                        <label className={labelCls}>Training track</label>
                        <select
                            data-testid="banner-athlete-track"
                            value={athleteEdit.track}
                            onChange={(e) => setAthleteEdit({ ...athleteEdit, track: e.target.value })}
                            className={inputCls}
                            autoFocus
                        >
                            <option value="">Select track…</option>
                            {TRACK_OPTIONS.map((t) => (
                                <option key={t.v} value={t.v}>{t.label}</option>
                            ))}
                        </select>
                        <p className="text-[11px] text-zinc-500 mt-2">
                            Not sure? Your coach can adjust later.
                        </p>
                    </div>
                )}

                <div className="mt-5 flex gap-2 justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-white px-3 py-2"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        disabled={busy}
                        data-testid="banner-save"
                        className="text-[10px] tracking-[0.2em] uppercase border border-brand-red bg-brand-red text-white px-4 py-2 rounded hover:bg-brand-red/90 disabled:opacity-50 transition-colors inline-flex items-center gap-1.5"
                    >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
}
